import { useCallback, useRef } from 'react';
import type { DiceResult, DicePart } from '../types';
import { randomInt, timeStr } from '../utils';

export interface UseDiceReturn {
  roll: (formula: string) => DiceResult | null;
  rollSingle: (dieName: string) => DiceResult | null;
  history: React.MutableRefObject<DiceResult[]>;
}

export function useDice(): UseDiceReturn {
  const history = useRef<DiceResult[]>([]);

  const roll = useCallback((formula: string): DiceResult | null => {
    formula = formula.trim().toLowerCase();
    if (!formula) return null;

    let total = 0;
    const details: string[] = [];
    const parts: DicePart[] = [];

    const tokens = formula.match(/[+-]?\s*\d*d\d+(?:kh?\d+|kl\d+)?|[+-]?\s*\d+/gi);
    if (!tokens) return null;

    for (const raw of tokens) {
      const token = raw.replace(/\s/g, '');
      const diceMatch = token.match(/^([+-])?(\d*)d(\d+)(?:k[h]?(\d+))?(?:kl(\d+))?$/i);

      if (diceMatch) {
        const sign = diceMatch[1] === '-' ? -1 : 1;
        const count = parseInt(diceMatch[2]) || 1;
        const sides = parseInt(diceMatch[3]);
        if (count > 100 || sides > 1000) continue;

        const rolls: number[] = [];
        for (let i = 0; i < count; i++) {
          rolls.push(randomInt(1, sides));
        }

        let kept = [...rolls];
        const keepHighest = diceMatch[4] ? parseInt(diceMatch[4]) : null;
        const keepLowest = diceMatch[5] ? parseInt(diceMatch[5]) : null;
        if (keepHighest && keepHighest < rolls.length) {
          kept = [...rolls].sort((a, b) => b - a).slice(0, keepHighest);
        } else if (keepLowest && keepLowest < rolls.length) {
          kept = [...rolls].sort((a, b) => a - b).slice(0, keepLowest);
        }

        const sum = kept.reduce((a, b) => a + b, 0) * sign;
        total += sum;
        const rollStr = rolls.map(r => kept.includes(r) ? String(r) : `~~${r}~~`).join(', ');
        details.push(`${count}d${sides}[${rollStr}]=${sum}`);
        parts.push({ type: 'dice', count, sides, rolls, kept, sum });
      } else {
        const mod = parseInt(token);
        if (!isNaN(mod)) {
          total += mod;
          details.push(`${mod >= 0 ? '+' : ''}${mod}`);
          parts.push({ type: 'mod', value: mod });
        }
      }
    }

    const result: DiceResult = {
      formula,
      parts,
      total,
      details: details.join(' '),
      timestamp: timeStr(),
    };

    history.current = [result, ...history.current].slice(0, 50);
    return result;
  }, []);

  const rollSingle = useCallback((dieName: string): DiceResult | null => {
    const sides = parseInt(dieName.replace('d', ''));
    return roll(`1d${sides}`);
  }, [roll]);

  return { roll, rollSingle, history };
}
